import { useToken } from '@hub/design-system-base'
import { Box } from '@hub/box'
import { IconButton } from '@hub/button'
import { ChevronLeftSmallIcon, ChevronRightSmallIcon } from '@hub/icon'
import { Imposter } from '@hub/imposter'
import { Reel, ReelProps } from '@hub/reel'
import React from 'react'
// useMobileBreakpoint looks unavoidable here due to the props composition
// and complex logic
// eslint-disable-next-line no-restricted-syntax
import useMobileBreakpoint from '../../helpers/use-mobile-breakpoint'

const LinkReel: React.FC<
  React.PropsWithChildren<
    Omit<ReelProps, 'visibleFrames' | 'renderControls'> & { isMobile?: boolean }
  >
> = props => {
  const isMobileBreakpoint = useMobileBreakpoint()
  const isMobile =
    props.isMobile === undefined ? isMobileBreakpoint : props.isMobile
  const surfaceTeriary = useToken('colors', 'surfaceTertiary')
  return (
    <Reel
      {...props}
      visibleFrames={0}
      sx={{
        ...props.sx,
        marginLeft: ['-spacing-lg', '-spacing-lg', 0],
        marginRight: ['-spacing-lg', '-spacing-lg', 0],
        scrollbarWidth: 'none',
        ...(isMobile && {
          width: 'auto',
          paddingRight: 'spacing-lg',
          paddingLeft: 'spacing-lg',
          WebkitMaskImage: `linear-gradient(
                to right,
                rgba(0,0,0,0) 2px, rgba(0,0,0,1) 24px,
                rgba(0,0,0,1) calc(100% - 24px), rgba(0,0,0,0) calc(100% - 2px)
              )`,
        }),
      }}
      renderControls={({
        scrollPosition,
        children,
        prevComponentProps,
        nextComponentProps,
      }) => (
        <Imposter
          impose={
            !isMobile && scrollPosition !== 'full' ? (
              <Box
                sx={{
                  backgroundColor: surfaceTeriary,
                  height: '100%',
                  paddingLeft: 'spacing-sm',
                  visibility: scrollPosition === 'end' ? 'hidden' : 'visible',
                  zIndex: 1,
                }}
              >
                <IconButton
                  {...nextComponentProps}
                  icon={<ChevronRightSmallIcon />}
                  variant="solid"
                  colorScheme="surfaceTertiaryWithIcon"
                  size="sm"
                  aria-label="Scroll to next"
                  aria-hidden="true"
                  sx={{
                    aspectRatio: '1',
                    minWidth: 'unset',
                    borderRadius: '50%',
                    display: ['none', 'flex'],
                    position: 'relative',
                    top: 'calc(50% - 18px)',
                  }}
                />
              </Box>
            ) : (
              <></>
            )
          }
          position="right"
        >
          <Imposter
            impose={
              !isMobile && scrollPosition !== 'full' ? (
                <Box
                  sx={{
                    backgroundColor: surfaceTeriary,
                    height: '100%',
                    paddingRight: 'spacing-sm',
                    visibility:
                      scrollPosition === 'start' ? 'hidden' : 'visible',
                    zIndex: 1,
                  }}
                >
                  <IconButton
                    {...prevComponentProps}
                    icon={<ChevronLeftSmallIcon />}
                    variant="solid"
                    colorScheme="surfaceTertiaryWithIcon"
                    size="sm"
                    aria-label="Scroll to previous"
                    aria-hidden="true"
                    sx={{
                      aspectRatio: '1',
                      minWidth: 'unset',
                      borderRadius: '50%',
                      display: ['none', 'flex'],
                      position: 'relative',
                      top: 'calc(50% - 18px)',
                    }}
                  />
                </Box>
              ) : (
                <></>
              )
            }
            position="left"
          >
            {children}
          </Imposter>
        </Imposter>
      )}
    >
      {props.children}
    </Reel>
  )
}

export default LinkReel
